import { ColumnDef, Row, SortingFn } from '@tanstack/react-table';
import { FieldValues, UseFormGetValues } from 'react-hook-form';

import { getSortingFunction } from '@/services/angularjs-bridge';
import { logger } from '@/services/logger';
import { DataModelAttribute, DataModelAttributeTypeName } from '@/types/DataModel';
import { Organization } from '@/types/Organization';
import { User } from '@/types/User';

import { MultiDimensionalActionCell } from '../MultiDimensional/MultiDimensionalActionColumn';
import { MultiReferenceActionCell } from '../MultiReference/components/MultiReferenceActionCell';
import { ValidationStateCell } from '../MultiReference/ValidationStateCell';
import { Cell } from './Cell';
import { CollectionDataType } from './Collection';
import { CollectionActionCell } from './CollectionActionCell';
import { Head } from './Head';

// Attributes that can not be rendered in a grid
const NON_SUPPORTED_ATTRIBUTES = [
    DataModelAttributeTypeName.Collection,
    DataModelAttributeTypeName.Composite,
    DataModelAttributeTypeName.MultiDimensional,
    DataModelAttributeTypeName.MultiReference,
] as string[];

const actionsColSize = 96;

export function generateCollectionAttributeColumns<TData>(
    collectionAttribute: DataModelAttribute,
    columnAttributes: DataModelAttribute[],
    collectionAttributeFieldPath: string,
    fields: Record<'id', string>[],
    organization: Organization | undefined,
    remove: (rowIndex: number) => void,
    type: DataModelAttributeTypeName,
    user: User | undefined,
    keyMembers: string[] = [],
    readonly?: boolean,
    t?: (key: string) => string,
    getValues?: UseFormGetValues<FieldValues>,
): ColumnDef<TData>[] {
    const sortingOptions = collectionAttribute.params?.uiCollectionSorter;
    // @ts-ignore
    const columns: ColumnDef<TData>[] = columnAttributes.reduce((list, attribute) => {
        if (NON_SUPPORTED_ATTRIBUTES.includes(attribute.typeName || 'undefined_type_name')) {
            return list;
        }

        const memberAttributeSortingOptions: {
        enableSortingRemoval: boolean;
        sortDescFirst: boolean;
        sortingFn?: SortingFn<CollectionDataType> | 'auto';
    } = {
        enableSortingRemoval: true,
        sortDescFirst: false,
        sortingFn: 'auto',
    };

        if (sortingOptions) {
            const sortingOption = sortingOptions[attribute.name];
            if (sortingOption) {
                memberAttributeSortingOptions.enableSortingRemoval = sortingOption.suppressRemoveSort || true;

                if (sortingOption.initialDirection === 'desc') {
                    memberAttributeSortingOptions.sortDescFirst = true;
                }

                if (sortingOption.sortingAlgorithm) {
                    const customSortingFn = getSortingFunction<CollectionDataType, string>(
                        sortingOption.sortingAlgorithm,
                    );

                    if (customSortingFn) {
                        const sortingFn: SortingFn<CollectionDataType> = (
                            rowA: Row<CollectionDataType>,
                            rowB: Row<CollectionDataType>,
                        ) => {
                            return customSortingFn(
                                rowA.original[attribute.name],
                                rowB.original[attribute.name],
                                rowA.original,
                                rowB.original,
                                'asc',
                                attribute,
                                collectionAttribute,
                                user,
                                organization,
                            );
                        };
                        memberAttributeSortingOptions.sortingFn = sortingFn;
                    } else {
                        logger.warn(
                            `columns: Sorting function '${sortingOption.sortingAlgorithm}' is defined in attribute '${collectionAttribute.name}'
                for member attribute '${attribute.name}' but was not found!`,
                        );
                    }
                }
            }
        }

        !(type === DataModelAttributeTypeName.MultiReference && attribute.name === 'action__') &&
        list.push({
            // @ts-ignore
            accessorFn: (row) => row[attribute.name],
            // @ts-ignore
            header: ({ column }) => <Head column={column} isReadonlyByParent={readonly} />,
            size: 200,
            meta: {
                attribute,
                collectionAttributeFieldPath,
                cellClassName: '!tw-text-t-primary',
                fields,
                isKeyMember: keyMembers.includes(attribute.name),
                readonly,
                memoizeCells: true,
                getValues,
            },
            // @ts-ignore
            cell: Cell,
            id: attribute.name,
            ...memberAttributeSortingOptions,
        });

        return list;
    }, [] as ColumnDef<TData>[]);

    // Condition column (MultiReference only)
    type === DataModelAttributeTypeName.MultiReference &&
    columns.push({
        accessorKey: 'condition',
        header: (t ? t('CONDITION') : 'CONDITION'),
        meta: {
            collectionAttributeFieldPath,
            cellClassName: 'tw-bg-white tw-text-right pinned-left-of-actions tw-h-9',
            pinned: true,
            // Use the same default starting guess; pinned offset will remain visually OK
            pinnedPosition: readonly ? 0 : actionsColSize,
        },
        size: 117,
        enableResizing: false,
        enableSorting: false,
        cell: function ValidationState({ row }) {
            return <ValidationStateCell row={row} />;
        },
    });

    !readonly &&
    columns.push({
        accessorKey: 'actions',
        header: 'Actions',
        meta: {
            pinned: true,
            collectionAttributeFieldPath,
            cellClassName: 'tw-bg-white tw-bg-white tw-text-right tw-px-2',
            pinnedPosition: 0
        },
        size: actionsColSize,
        minSize: actionsColSize,
        maxSize: actionsColSize,
        enableResizing: false,
        enableSorting: false,
        cell: function ActionCell({ row, column }) {
            switch (type) {
                case DataModelAttributeTypeName.Collection:
                    return <CollectionActionCell row={row} column={column} remove={remove}/>;
                case DataModelAttributeTypeName.MultiReference:
                    return <MultiReferenceActionCell row={row} column={column} remove={remove}/>;
                default:
                    return <MultiDimensionalActionCell row={row} column={column} remove={remove}/>;
            }
        },
    });

    return columns;
}
